import { AdminPanel } from '@/components/AdminPanel'
import { GuardianBanner } from '@/components/GuardianBanner'
import { ModeSelector } from '@/components/ModeSelector'
import { Card } from '@/components/ui'
import type { ExecutionMode } from '@/lib/types'

/**
 * Paramètres.
 *
 * Ce qui se règle une fois par session, et non à chaque trade : le mode
 * d'exécution, le rappel des seuils de coupure automatique, et — pour
 * l'administration seulement — la gestion des traders.
 *
 * Ces éléments occupaient le haut de l'écran de trading, où ils repoussaient
 * le graphique vers le bas sans jamais servir. Le bouton « Tout couper », lui,
 * a rejoint l'onglet Positions : c'est là qu'il agit.
 */
export function SettingsScreen({
  mode,
  onChangeMode,
  brokerAvailable,
  brokerReady,
  role,
  username,
  onLogout,
  ctraderReturn = null,
  onAccountsChanged,
}: {
  mode: ExecutionMode
  onChangeMode: (mode: ExecutionMode) => void
  brokerAvailable: boolean
  brokerReady: boolean
  role: string | null
  username: string | null
  onLogout: () => void
  /** Résultat du retour de la page d'autorisation cTrader, à afficher. */
  ctraderReturn?: { ok: boolean; detail: string } | null
  /** À appeler quand les comptes ont changé, pour rafraîchir le sélecteur. */
  onAccountsChanged?: () => void
}) {
  return (
    <div className="flex flex-col gap-3">
      {/*
        Qui est connecté, et de quoi se déconnecter. Placé en tête : sur un
        outil que plusieurs personnes utilisent, savoir sous quel compte on se
        trouve est la première question — surtout quand un même téléphone sert
        à vérifier le compte d'un trader puis le sien.
      */}
      <Card title="Session">
        <div className="flex flex-wrap items-center justify-between gap-3 p-3">
          <p className="text-[13px] text-ink">
            Connecté en tant que{' '}
            <span className="num font-semibold text-ink-strong">{username ?? '—'}</span>
            {role === 'admin' && (
              <span className="ml-2 rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                ADMIN
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={onLogout}
            className="min-h-touch rounded-md border border-border bg-elevated px-4 text-[13px] font-semibold text-ink hover:bg-border/60 active:bg-border"
          >
            Se déconnecter
          </button>
        </div>
      </Card>

      <Card title="Mode d'exécution">
        <div className="p-3">
          <ModeSelector
            mode={mode}
            onChange={onChangeMode}
            brokerAvailable={brokerAvailable}
          />
        </div>
      </Card>

      <Card title="Coupure automatique">
        <div className="p-3">
          {/* Le bandeau porte aussi « Tout couper » ; il n'est montré qu'en
              lecture ici, l'action vit dans l'onglet Positions. */}
          <GuardianBanner brokerReady={brokerReady} readOnly />
          <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">
            Ces seuils sont surveillés <strong className="text-ink">sur le serveur</strong>,
            toutes les 0,5 seconde. Ils continuent de protéger le compte téléphone éteint.
            Pour couper immédiatement, le bouton est dans l'onglet Positions.
          </p>
        </div>
      </Card>

      {role === 'admin' && (
        <AdminPanel
          currentUsername={username}
          ctraderReturn={ctraderReturn}
          onAccountsChanged={onAccountsChanged}
        />
      )}

      {/* Crédit de la bibliothèque de graphiques. Il était posé SUR le
          graphique, où il recouvrait l'étiquette du stop ; il est ici. */}
      <p className="px-1 pb-2 text-center text-[11px] text-ink-muted">
        Graphiques :{' '}
        <a
          href="https://www.tradingview.com/lightweight-charts/"
          target="_blank"
          rel="noreferrer noopener"
          className="underline hover:text-ink"
        >
          Lightweight Charts™ de TradingView
        </a>
      </p>
    </div>
  )
}
